import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageLoader, formatDate, formatTime } from "@/components/common";
import { CalendarHeart, MapPin, ArrowRight } from "lucide-react";

function OutingCard({ o }) {
    return (
        <Link to={`/outings/${o.id}`} data-testid={`outing-card-${o.id}`}>
            <div className="flex items-center gap-4 rounded-2xl bg-card p-5 shadow-sm">
                <div className="grid h-14 w-14 flex-none place-items-center rounded-2xl bg-secondary text-2xl">{o.activity_emoji}</div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <p className="truncate font-display text-lg font-700 text-primary">{o.activity_name}</p>
                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-700 uppercase text-muted-foreground">{o.status}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{formatDate(o.date)} · {formatTime(o.start_time)}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                        <span className="mr-1">{o.my_identity?.temp_emoji} {o.my_identity?.temp_name}</span>
                        {o.venue_name && <><MapPin className="h-3 w-3" /> {o.venue_name}</>}
                    </p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
        </Link>
    );
}

export default function MyOutings() {
    const { data } = useQuery({ queryKey: ["my-outings"], queryFn: async () => (await api.get("/me/outings")).data });
    if (!data) return <PageLoader />;
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = data.filter((o) => o.status !== "completed" && (o.date || "") >= today && o.my_attendance !== "canceled");
    const past = data.filter((o) => o.status === "completed" || (o.date || "") < today || o.my_attendance === "canceled");

    return (
        <div className="space-y-6">
            <h1 className="font-display text-2xl font-700 tracking-tight text-primary">My Outings</h1>
            {data.length === 0 && (
                <div className="flex flex-col items-center rounded-2xl bg-card p-10 text-center shadow-sm">
                    <CalendarHeart className="h-10 w-10 text-accent" />
                    <p className="mt-3 font-600 text-primary">No outings yet</p>
                    <p className="mt-1 text-sm text-muted-foreground">Tap "Find Something to Do" to join your first group.</p>
                    <Link to="/discover"><span className="mt-4 inline-block rounded-full bg-accent px-5 py-2 text-sm font-600 text-accent-foreground">Find Something to Do</span></Link>
                </div>
            )}
            {upcoming.length > 0 && (
                <section>
                    <h2 className="mb-3 text-sm font-700 uppercase tracking-wide text-muted-foreground">Upcoming</h2>
                    <div className="space-y-3">{upcoming.map((o) => <OutingCard key={o.id} o={o} />)}</div>
                </section>
            )}
            {past.length > 0 && (
                <section>
                    <h2 className="mb-3 text-sm font-700 uppercase tracking-wide text-muted-foreground">Past</h2>
                    <div className="space-y-3 opacity-90">{past.map((o) => <OutingCard key={o.id} o={o} />)}</div>
                </section>
            )}
        </div>
    );
}
